import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTour } from '@reactour/tour';
import { selectUserInfo } from 'redux/userInfo/userInfoSlice';
import { useTourDriver } from './tour/useTourDriver';
import { getActiveTourKey } from './tour/activeTour';
import completeIntro from 'api/login/completeIntro';

const INTRO_TOUR_KEY = 'canapps_navigation';

// Auto-starts the CANapps Home Tour walkthrough once for a first-time user
// (Users.intro_completed = 0, surfaced as userData.introCompleted). When that
// auto-started run closes — finished or skipped — we persist completion so it
// never auto-starts again. Manual replays from the "?" checklist are untouched:
// we only arm completion for a run WE started.
//
// Rendered inside the router so it can use the tour driver's navigation.
const IntroTourAutostart = () => {
	const userData = useSelector(selectUserInfo)?.userInfo;
	const { startTour } = useTourDriver();
	const { isOpen } = useTour();

	const startedRef = useRef(false);
	const awaitingCloseRef = useRef(false);

	// Kick off the intro once, after user data has loaded for a first-timer.
	useEffect(() => {
		if (startedRef.current) return;
		const userId = userData?.userId;
		if (!userId || userId === '') return;
		// Only auto-start when we positively know they haven't seen it.
		if (userData?.introCompleted !== false) return;
		// startTour is blocked on narrow screens (returns false); don't burn the
		// one-shot then — startTour's identity changes when the viewport crosses
		// 'md', so this effect re-runs and retries once it's wide enough.
		if (startTour(INTRO_TOUR_KEY)) startedRef.current = true;
	}, [userData?.userId, userData?.introCompleted, startTour]);

	// Arm on open (only for our intro run), persist on the following close.
	useEffect(() => {
		if (isOpen) {
			if (startedRef.current && getActiveTourKey() === INTRO_TOUR_KEY) {
				awaitingCloseRef.current = true;
			}
		} else if (awaitingCloseRef.current) {
			awaitingCloseRef.current = false;
			completeIntro();
		}
	}, [isOpen]);

	return null;
};

export default IntroTourAutostart;
